import styled, { keyframes } from "styled-components";
import { fontMontserrat } from "@/assets/styles/abstract/variables";

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
`;

const slideIn = keyframes`
  from { transform: translateY(50px) scale(0.9); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
`;

const soundWave = keyframes`
  0% { height: 3px; }
  50% { height: 20px; }
  100% { height: 3px; }
`;

export const SpeechReaderStyle = styled.div`
  position: fixed;
  bottom: 25px;
  right: 25px;
  z-index: 999999;
  font-family: ${fontMontserrat}, sans-serif;
  animation: ${slideIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;

  .speech-widget {
    background: rgba(21, 82, 61, 0.85); /* TDTU Green with transparency */
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 16px;
    padding: 16px 20px;
    width: 320px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    color: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transition: all 0.3s ease;
  }

  .speech-widget__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 10px;

    .title {
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .close-btn {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      border-radius: 50%;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #ffffff;
      }
    }
  }

  .speech-widget__body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .speech-status {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;
    min-height: 34px;
    display: flex;
    align-items: center;
  }

  .speech-wave {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    height: 24px;
    width: 40px;

    span {
      display: inline-block;
      width: 3px;
      height: 3px;
      background-color: #3b82f6; /* Beautiful blue */
      border-radius: 2px;
      
      &.active {
        animation: ${soundWave} 1s ease-in-out infinite;
        
        &:nth-child(1) { animation-delay: 0.1s; }
        &:nth-child(2) { animation-delay: 0.3s; }
        &:nth-child(3) { animation-delay: 0.5s; }
        &:nth-child(4) { animation-delay: 0.2s; }
        &:nth-child(5) { animation-delay: 0.4s; }
      }
    }
  }

  .speech-widget__controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 5px;
  }

  .control-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .btn-circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    &.btn-primary {
      background: #3b82f6;
      color: #ffffff;

      &:hover {
        background: #2563eb;
        box-shadow: 0 0 12px rgba(59, 130, 246, 0.4);
      }
    }

    &.btn-danger {
      background: #ef4444;
      color: #ffffff;

      &:hover {
        background: #dc2626;
        box-shadow: 0 0 12px rgba(239, 68, 68, 0.4);
      }
    }
  }

  .speed-selector {
    position: relative;
    
    .speed-btn {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      color: #ffffff;
      padding: 6px 12px;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
      }
    }

    .speed-dropdown {
      position: absolute;
      bottom: 120%;
      right: 0;
      background: rgba(21, 82, 61, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 8px;
      padding: 4px;
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 70px;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
      z-index: 10;

      button {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.8);
        padding: 6px 10px;
        font-size: 11px;
        font-weight: 500;
        text-align: center;
        width: 100%;
        cursor: pointer;
        border-radius: 4px;
        transition: all 0.2s;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #ffffff;
        }

        &.active {
          background: #3b82f6;
          color: #ffffff;
          font-weight: 600;
        }
      }
    }
  }
`;

export const SpeechHeaderButtonStyle = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 50%;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  position: relative;
  outline: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #3b82f6;
  }

  &.active {
    color: #3b82f6;
    animation: ${pulse} 2s infinite;
  }

  .tooltip-text {
    visibility: hidden;
    width: 120px;
    background-color: rgba(0, 0, 0, 0.8);
    color: #fff;
    text-align: center;
    border-radius: 4px;
    padding: 5px 0;
    position: absolute;
    z-index: 1;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.3s;
    font-size: 10px;
    font-family: ${fontMontserrat}, sans-serif;
    pointer-events: none;
  }

  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`;
